import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";

const INTERESTS = ["GPU & AI Compute", "Computer Components", "Memory & Storage", "Servers & Workstations", "Networking", "Consumer Technology", "Mobile & Accessories", "Gaming / Consoles", "All Offers"];

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [sub, setSub] = useState(null);
  const [interests, setInterests] = useState([]);
  const [missing, setMissing] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!token) { setMissing(true); return; }
    api.get("/subscribe/preferences", { params: { token } })
      .then((r) => { setSub(r.data); setInterests(r.data.interests || []); })
      .catch(() => setMissing(true));
  }, [token]);

  const toggle = (i) => setInterests((arr) => (arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i]));

  const savePreferences = async () => {
    try {
      await api.post("/subscribe/preferences", { token, interests });
      setSaved(true);
      toast.success("Preferences updated");
    } catch {
      toast.error("Could not update preferences.");
    }
  };

  const doUnsubscribe = async () => {
    try {
      await api.post("/unsubscribe", { token });
      setUnsubscribed(true);
    } catch {
      toast.error("Could not process unsubscribe.");
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="unsubscribe-page">
      <Seo title="Subscription Preferences — Royal Deals FZ-LLC" description="Manage your Royal Deals FZ-LLC subscription preferences." robots="noindex, nofollow" />
      <Nav />
      <main className="pt-44 pb-24">
        <div className="max-w-[640px] mx-auto px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-6">Subscription Preferences</p>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-midnight">Manage your preferences.</h1>

          {missing && (
            <p className="text-midnight/60 mt-8 leading-relaxed" data-testid="unsubscribe-missing">
              This subscription link is invalid or has expired. Contact us at info@royaldealsfzllc.com and we will update your preferences manually.
            </p>
          )}

          {unsubscribed && (
            <div className="mt-8 border border-gold/40 bg-gold/[0.05] p-8" data-testid="unsubscribe-done">
              <p className="text-midnight/75 leading-relaxed">You have been unsubscribed from Royal Deals FZ-LLC marketing communications. You can re-subscribe at any time from our website.</p>
            </div>
          )}

          {sub && !unsubscribed && (
            <div className="mt-10 space-y-8" data-testid="preferences-panel">
              <div className="bg-[#FAFAFA] hairline p-6">
                <p className="text-sm text-midnight/60">Subscription for <span className="font-medium text-midnight">{sub.email}</span></p>
                <p className="text-xs text-midnight/45 mt-1">Status: {sub.status === "subscribed" ? "Subscribed" : "Unsubscribed"}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-midnight/50 mb-4">Your interests</p>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((i) => (
                    <button
                      key={i}
                      onClick={() => toggle(i)}
                      data-testid={`pref-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`text-xs font-medium px-4 py-2 transition-colors ${interests.includes(i) ? "bg-midnight text-white" : "bg-white text-midnight/60 hairline hover:border-gold"}`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
                <button onClick={savePreferences} data-testid="preferences-save" className="mt-6 bg-midnight text-white text-sm font-medium px-8 py-3 hover:bg-midnight-light transition-colors">
                  {saved ? "Saved" : "Save Preferences"}
                </button>
              </div>
              <div className="border-t border-midnight/10 pt-6">
                <p className="text-sm text-midnight/55 mb-4">Prefer to stop all marketing communications?</p>
                <button onClick={doUnsubscribe} data-testid="unsubscribe-button" className="text-sm font-medium text-red-600 border border-red-200 px-6 py-2.5 hover:bg-red-50 transition-colors">
                  Unsubscribe from all emails
                </button>
              </div>
            </div>
          )}

          <p className="mt-12 text-sm text-midnight/45">
            <Link to="/" className="text-gold hover:text-midnight transition-colors">← Back to Royal Deals FZ-LLC</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
